import { useStore } from 'stimulus-store';
import TomSelect from "tom-select";
import { getArea, getAreasForSelect } from "shared/locales/locales";
import { updateSelect } from "shared/selects/select_helpers";
import { isEmpty } from "lodash";
import { Controller } from "@hotwired/stimulus";
import { areaStore } from "../../../stores/locales/area_store";
import { DO_NOT_EMIT_EVENTS } from "constants/select_constants";
import { LOCALE_TYPE_REGION, LOCALE_TYPE_BUILDING, LOCALE_TYPE_AREA } from "constants/facilities_constants";

// Connects to data-controller="selects--locales--area-select"
//
// TURBO DRIVE NOTE: areaStore is a module-level singleton that persists across
// Turbo navigations. This controller clears it in disconnect() when this
// controller's element is torn down (e.g. on Turbo Drive navigation). See
// region_select_controller.js for the full rationale.
export default class extends Controller {
    _region
    _building

    static stores = [
        areaStore
    ]

    static values = {
        parentBuilding: String,
        parentRegion: String,
        selected: String,
        syncUrl: Boolean,

        // See region_select_controller.js for the full rationale: set only by pages
        // that need organization-wide (not active-project) results, e.g. the Reports
        // filter builder. Left unset, this controller's behavior is unchanged.
        projectScopeToken: String
    }

    connect() {
        useStore(this);
    }

    disconnect() {
        this.resetAreaStore();
    }

    initialize() {
        this._createAreaSelect();
    }

    _createAreaSelect() {
        // Get the area select and the region and building ids
        const areaSelectElement = this.element;
        this._region = this.parentRegionValue;
        this._building = this.parentBuildingValue;
        const areaSelectId = this.selectedValue;

        // Fall back to URL params if not server-rendered (independent hydration)
        const params = new URLSearchParams(window.location.search);
        const regionFromUrl = params.get(LOCALE_TYPE_REGION);
        const buildingFromUrl = params.get(LOCALE_TYPE_BUILDING);
        if (isEmpty(this._region) && regionFromUrl) {
            this._region = regionFromUrl;
        }
        if (isEmpty(this._building) && buildingFromUrl) {
            this._building = buildingFromUrl;
        }

        // Require a region and building to be selected to fetch areas
        if (!isEmpty(this._region) && !isEmpty(this._building)) {
            // Fetch this region's buildings
            getAreasForSelect(this._region, this._building, this.projectScopeTokenValue).then((results) => {
                const control = new TomSelect(areaSelectElement, {
                    placeholder: 'Select an area',
                    create: false,
                    allowClear: true,
                    valueField: 'id',
                    labelField: 'text',
                    // Match on the area name AND its floor so users can filter by either.
                    searchField: ['text', 'floor'],
                    sortField: {
                        field: "text",
                        direction: "asc"
                    },
                    onChange: (event) => this._areaSelectionChanged(event), // Using an arrow function here allows us to reference methods in the class using this
                    onDelete: (event) => this._areaUnselectedEvent(event),  // Keep this separate or page loads will be broken
                    // options: results,
                    maxOptions: null, // tom-select will truncate the select to 50 options by default
                    plugins: ['remove_button'], // This enables a "clear" button on selected items
                    closeAfterSelect: true, // This closes the select after a selection is made.
                    // Group areas under a header for their floor. Each area option
                    // carries a `floor` field; bucketing by it renders a "Floor N"
                    // header above that floor's areas. See _floorOptgroupConfig.
                    ...this._floorOptgroupConfig()
                });

                // Syncing will ensure options are stubbed out within the select element. This
                // ensures the tests will have the data in the DOM it can test against
                control.sync();

                // Add the expected options to the select. We're doing it this way instead of
                // in the constructor so the items appear in the underlying select. This makes
                // it possible to test using capybara matchers
                updateSelect(this.element.id, results);

                // Register one optgroup per distinct floor so the dropdown shows a
                // floor header above each floor's areas. updateSelect only manages
                // options, not groups, so the area controller owns this.
                this._applyFloorOptgroups(control, results);

                // Pre-selection logic: server-rendered selectedValue takes priority over URL param
                const areaFromUrl = params.get(LOCALE_TYPE_AREA);
                if (areaSelectId !== null && areaSelectId !== '') {
                    // Existing: server-rendered pre-selection
                    // Setting silent to true suppresses change events
                    // https://tom-select.js.org/docs/api/
                    control.setValue(areaSelectId, DO_NOT_EMIT_EVENTS);

                    // Set the store synchronously from the option first (see
                    // _areaSelectionChanged), then enrich with the full area metadata.
                    const preselectedOption = control.options[areaSelectId];
                    this.setAreaStoreValue({ id: areaSelectId, name: preselectedOption ? preselectedOption.text : "" });
                    getArea(areaSelectId).then((area) => {
                        this.setAreaStoreValue(area);
                    });
                } else if (areaFromUrl) {
                    // URL-parameter pre-selection (independent hydration)
                    const selectedOption = control.options[areaFromUrl];
                    if (selectedOption) {
                        control.setValue(areaFromUrl, DO_NOT_EMIT_EVENTS);
                        // Synchronous set from the option first, then enrich (see above).
                        this.setAreaStoreValue({ id: areaFromUrl, name: selectedOption.text });
                        getArea(areaFromUrl).then((area) => {
                            this.setAreaStoreValue(area);
                        });
                    } else {
                        // Invalid area ID in URL — clean up
                        this._removeAreaIdFromUrl();
                    }
                }
            }).catch(() => {
                this._removeAreaIdFromUrl();
            });
        } else {
            // Check if area URL param is orphaned (present without both region and building)
            const areaFromUrl = params.get(LOCALE_TYPE_AREA);
            if (areaFromUrl && (isEmpty(this._region) || isEmpty(this._building))) {
                this._removeAreaIdFromUrl();
            }

            const control = new TomSelect(areaSelectElement, {
                placeholder: 'Select an area',
                create: false,
                allowClear: true,
                valueField: 'id',
                labelField: 'text',
                // Match on the area name AND its floor so users can filter by either.
                searchField: ['text', 'floor'],
                sortField: {
                    field: "text",
                    direction: "asc"
                },
                onChange: (event) => this._areaSelectionChanged(event), // Using an arrow function here allows us to reference methods in the class using this
                onDelete: (event) => this._areaUnselectedEvent(event),  // Keep this separate or page loads will be broken
                maxOptions: null, // tom-select will truncate the select to 50 options by default
                plugins: ['remove_button'], // This enables a "clear" button on selected items
                closeAfterSelect: true, // This closes the select after a selection is made.
                // Group areas under a floor header (see _floorOptgroupConfig).
                ...this._floorOptgroupConfig()
            });

            // Disable the building select since the user hasn't selected a region or building yet
            this.element.disabled = true;
            if (this.element.tomselect) {
                this.element.tomselect.disable();
            }
        }

    }

    _areaSelectionChanged(areaId) {
        // We only want to process events where the user made a selection. Ignore
        // other events. This happens during initialization.
        if (isEmpty(areaId)) {
            return;
        }

        // Set the store SYNCHRONOUSLY from the already-loaded option first, so consumers
        // that only need the selected id (e.g. the reports builder assembling filters on
        // Run Report) see it immediately and do not race the getArea fetch below. Region
        // and building controllers set their stores synchronously the same way; area used
        // to defer entirely until getArea resolved, which dropped the area filter when a
        // report was run before the fetch completed.
        const selectedOption = this.element.tomselect ? this.element.tomselect.options[areaId] : null;
        this.setAreaStoreValue({ id: areaId, name: selectedOption ? selectedOption.text : "" });

        // Enrich the store with the full area object (region/building ids etc.) once it
        // resolves, for consumers like the device manager that need the full hierarchy.
        getArea(areaId).then((area) => {
            this.setAreaStoreValue(area);
        });

        /*
         * Send an event telling the area and room select controllers to enable
         * those elements. I stole this method from here:
         * https://lmika.org/2021/02/17/communication-among-stimulus.html
         */
        window.dispatchEvent(new CustomEvent('areaSelectionMade', {
            detail: {
                region: this._region,
                building: this._building,
                area: areaId
            },
            bubbles: true // This needs to be explicitly set to enable event bubbling
        }));

        if (this.syncUrlValue) {
            this._addAreaIdToUrl(areaId);
        }
    }

    _areaUnselectedEvent(values) {
        // Clear the area store. The Print Mapping widget listens to
        // area store updates to change the printer selects.
        this.setAreaStoreValue({});

        // If we don't have an ID, clear out the current selections and return
        window.dispatchEvent(new Event('areaSelectionCleared', {
            detail: {},
            bubbles: true // This needs to be explicitly set to enable event bubbling
        }));

        if (this.syncUrlValue) {
            this._removeAreaIdFromUrl();
        }
    }

    handleRegionChanged(event) {
        // Return if this event is empty. This may happen during initialization.
        if (event === undefined) {
            return;
        }

        // Get the new selected region ID from the event
        this._region = event.detail.region;
        this._building = null;

        // Disable the area select since building is no longer selected
        this.element.disabled = true;
        if (this.element.tomselect) {
            this.element.tomselect.disable();
        }

        // Clear out the area select. Changing the region must mean no building is selected
        updateSelect(this.element.id, []);

        // Clear the area store since the area is no longer valid
        this.setAreaStoreValue({});
    }

    handleRegionCleared(event) {
        // Clear the variable that stores the region and building ids
        this._region = null;
        this._building = null;

        // Disable the area select
        this.element.disabled = true;
        if (this.element.tomselect) {
            this.element.tomselect.disable();
        }

        // Clear the buildings
        updateSelect(this.element.id, []);

        // Clear the area store since the area is no longer valid
        this.setAreaStoreValue({});
    }

    handleBuildingChange(event) {
        // Update the selected building
        this._building = event.detail.building;

        // Get the new building's areas
        getAreasForSelect(this._region, this._building, this.projectScopeTokenValue).then((results) => {
            updateSelect(this.element.id, results)

            // Rebuild the floor optgroups for the newly loaded areas.
            this._applyFloorOptgroups(this.element.tomselect, results);

            // Enable the area select only once its options are actually
            // populated. Enabling synchronously before this resolved meant a
            // fast click (real user or automated test) could open the
            // dropdown while it was still empty.
            this.element.disabled = false;
            if (this.element.tomselect) {
                this.element.tomselect.enable();
            }
        });
    }

    handleBuildingCleared(event) {
        // Clear the variable that stores the building id
        this._building = null;

        // Disable the area select
        this.element.disabled = true;
        if (this.element.tomselect) {
            this.element.tomselect.disable();
        }

        // Clear the buildings
        updateSelect(this.element.id, []);

        // Clear the area store since the area is no longer valid
        this.setAreaStoreValue({});
    }

    // Tom Select config that groups area options under a header for their floor.
    //
    // Each area option carries a `floor` field (see getAreasForSelect), so
    // pointing optgroupField at `floor` buckets options by floor. The groups
    // themselves are registered in _applyFloorOptgroups (keyed by `floor` too,
    // via optgroupValueField), each with a `label` the optgroup_header renders.
    // lockOptgroupOrder keeps groups in the $order we register them (sorted by
    // floor) rather than reordering as options are added.
    _floorOptgroupConfig() {
        return {
            optgroupField: 'floor',
            optgroupValueField: 'floor',
            optgroupLabelField: 'label',
            lockOptgroupOrder: true,
            render: {
                // Floor header: bold and a touch larger than the area-name options
                // below it, with a top border acting as a divider between
                // consecutive floor groups. The first group skips the border so the
                // dropdown doesn't open with a stray rule above the top header.
                // _firstFloorGroupKey is set when the groups are (re)registered in
                // _applyFloorOptgroups.
                optgroup_header: (data, escape) => {
                    const isFirst = data.floor === this._firstFloorGroupKey;
                    const divider = isFirst ? '' : 'border-top:1px solid var(--bs-border-color, #dee2e6);';
                    return `<div class="optgroup-header" style="font-size:1.1em;font-weight:bold;${divider}">${escape(data.label)}</div>`;
                },
                // Area option: indent the name under its floor header using CSS
                // padding, NOT leading whitespace in the label — the label value
                // (data.text) stays the plain area name so search matching and
                // copy-paste of the selected value are unaffected.
                option: (data, escape) =>
                    `<div style="padding-left:1em">${escape(data.text)}</div>`
            }
        };
    }

    // Floor value used to bucket areas that have no floor set. Keeping it a
    // non-empty sentinel (rather than null/'') means these areas still land in a
    // single labeled group instead of rendering ungrouped above every header.
    get _noFloorGroupKey() {
        return '__no_floor__';
    }

    // Registers one optgroup per distinct floor present in `results`, so the
    // dropdown renders a "Floor N" header above that floor's areas. Clears any
    // previously-registered groups first (updateSelect replaces the options but
    // leaves groups untouched), then refreshes the dropdown so headers render.
    //
    // Areas are grouped by the raw `floor` value, which must match each option's
    // optgroupField; areas with no floor are coerced to a single sentinel group
    // so they appear under one "No floor" header. Numeric floors sort ascending;
    // the no-floor group sorts last.
    _applyFloorOptgroups(control, results) {
        if (!control) {
            return;
        }

        control.clearOptionGroups();

        // Normalize each option's floor to the group key it should fall under,
        // so the option's optgroupField matches a registered group. Options were
        // already added by updateSelect, so update them in place.
        (results || []).forEach((area) => {
            const groupKey = isEmpty(area.floor) ? this._noFloorGroupKey : area.floor;
            const option = control.options[area.id];
            if (option) {
                option.floor = groupKey;
                control.updateOption(area.id, option);
            }
        });

        // Distinct floors present, sorted: numeric floors ascending, with the
        // no-floor bucket last.
        const floors = [...new Set((results || []).map((area) => (isEmpty(area.floor) ? this._noFloorGroupKey : area.floor)))];
        floors.sort((a, b) => {
            if (a === this._noFloorGroupKey) return 1;
            if (b === this._noFloorGroupKey) return -1;
            const na = Number(a);
            const nb = Number(b);
            if (!Number.isNaN(na) && !Number.isNaN(nb)) {
                return na - nb;
            }
            return String(a).localeCompare(String(b));
        });

        // Remember the first (top-most) floor group so its header can skip the
        // divider border — see the optgroup_header renderer.
        this._firstFloorGroupKey = floors.length > 0 ? floors[0] : null;

        floors.forEach((floor) => {
            const label = floor === this._noFloorGroupKey ? 'No floor' : `Floor ${floor}`;
            control.addOptionGroup(floor, { floor: floor, label: label });
        });

        // Re-render the dropdown so the newly registered group headers appear.
        control.refreshOptions(false);
    }

    _addAreaIdToUrl(areaId) {
        const params = new URLSearchParams(window.location.search);
        params.set(LOCALE_TYPE_AREA, areaId);
        this._replaceUrl(params);
    }

    _removeAreaIdFromUrl() {
        const params = new URLSearchParams(window.location.search);
        params.delete(LOCALE_TYPE_AREA);
        this._replaceUrl(params);
    }

    _replaceUrl(params) {
        const queryString = params.toString();
        const newUrl = queryString
            ? `${window.location.pathname}?${queryString}${window.location.hash}`
            : `${window.location.pathname}${window.location.hash}`;
        history.replaceState(null, '', newUrl);
    }
}
